// ============================================================
// TMR Portal Admin — a confirmação da consulta ao Serasa (26 §9)
// ============================================================
import { useState } from 'react';
import { ModalShell } from '../../components/ModalShell';
import { Btn } from '../../components/primitives';
import { Icons } from '../../lib/icons';

/** Props for {@link BureauConsultDialog}. */
export interface BureauConsultDialogProps {
  /** The CPF being consulted, masked. */
  document: string;
  /**
   * Runs the consult: `force` false reuses a report of the last 3 months, true pays for a new one. The dialog stays
   * open, disabled, until it settles — a second click would otherwise send a second request.
   */
  onConsult: (force: boolean) => Promise<unknown>;
  onClose: () => void;
}

/**
 * Confirms a consult to the Serasa, which may be paid. "Consultar" reuses the report of the last 3 months when there
 * is one, as the customer's next sale would; "Forçar nova consulta" pays for a new one regardless.
 */
export function BureauConsultDialog({ document, onConsult, onClose }: BureauConsultDialogProps) {
  const [pending, setPending] = useState<'reuse' | 'force' | null>(null);

  const run = async (force: boolean) => {
    if (pending) return;
    setPending(force ? 'force' : 'reuse');
    try {
      await onConsult(force);
    } finally {
      setPending(null);
    }
    onClose();
  };

  return (
    <ModalShell title="Consultar o Serasa?" lead={`CPF ${document}`} width={480} onClose={pending ? () => undefined : onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-muted)' }}>
        <p style={{ margin: 0 }}>
          <strong style={{ color: 'var(--ink-900)' }}>Consultar</strong> usa a consulta dos últimos 3 meses, quando houver, sem
          custo. Sem ela, faz uma consulta paga no nome do cliente.
        </p>
        <p style={{ margin: 0 }}>
          <strong style={{ color: 'var(--ink-900)' }}>Forçar nova consulta</strong> sempre paga uma consulta nova, mesmo com uma
          recente guardada. O resultado vale para a próxima venda deste CPF.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', marginTop: 22 }}>
        <Btn variant="ghost" onClick={onClose} disabled={pending !== null}>
          Cancelar
        </Btn>
        <Btn variant="secondary" icon={Icons.zap} onClick={() => void run(true)} disabled={pending !== null}>
          {pending === 'force' ? 'Consultando…' : 'Forçar nova consulta'}
        </Btn>
        <Btn variant="primary" icon={Icons.search} onClick={() => void run(false)} disabled={pending !== null}>
          {pending === 'reuse' ? 'Consultando…' : 'Consultar'}
        </Btn>
      </div>
    </ModalShell>
  );
}
